import * as React from 'react';

import {
  Button,
  Col,
  PhoneField,
  Row,
  Select,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideBuilding } from 'lucide-react';

import { FormSection } from '~/modules/settings/_components/_shared/FormSection';
import { SettingsContent } from '~/modules/settings/_components/_shared/SettingsContent';
import {
  $cpCurrentUser,
  $cpUserForm,
  $initCpUserForm,
  $isCpUserFormDirty,
  $updateCpUserForm,
} from '~/modules/settings/_state/customerPortalUserForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

export const CustomerPortalContactInfoTab = () => {
  const t = useTranslations();
  const toast = useToast();
  const currentUser = useAtomValue($cpCurrentUser);
  const form = useAtomValue($cpUserForm);
  const updateForm = useSetAtom($updateCpUserForm);
  const initForm = useSetAtom($initCpUserForm);
  const isFormDirty = useAtomValue($isCpUserFormDirty);

  const [isSaving, setIsSaving] = React.useState(false);

  const handleFieldChange =
    <K extends keyof typeof form>(field: K) =>
    (value: unknown) => {
      updateForm({ [field]: value as (typeof form)[K] });
    };

  const handleDiscard = () => {
    initForm();
  };

  const handleSave = async () => {
    if (!currentUser) return;
    setIsSaving(true);
    try {
      toast.success(
        t.customerPortal.userDetails.contactInfo.messages.updateSuccess,
      );
    } catch (_error) {
      toast.error(
        t.customerPortal.userDetails.contactInfo.messages.updateError,
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentUser) {
    return (
      <SettingsContent size="medium" className="p-5">
        <div className="text-sm text-neutral/50">
          {t.customerPortal.userDetails.loading}
        </div>
      </SettingsContent>
    );
  }

  return (
    <Col className="@CustomerPortalContactInfoTab h-full">
      <Col gap6 className="h-0 grow overflow-y-auto">
        <SettingsContent size="medium" className="gap-6 p-5">
          <FormSection
            icon={LucideBuilding}
            title={t.customerPortal.userDetails.contactInfo.sectionTitle}
          >
            <Col gap4>
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label={t.customerPortal.userDetails.fields.firstName}
                  value={form.firstName}
                  onChange={handleFieldChange('firstName')}
                  isRequired
                />
                <TextField
                  label={t.customerPortal.userDetails.fields.lastName}
                  value={form.lastName}
                  onChange={handleFieldChange('lastName')}
                  isRequired
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label={t.customerPortal.userDetails.fields.email}
                  value={form.email}
                  onChange={handleFieldChange('email')}
                  type="email"
                  isRequired
                />
                <PhoneField
                  label={t.customerPortal.userDetails.fields.phone}
                  value={form.phone}
                  onChange={handleFieldChange('phone')}
                  isRequired
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Select
                  aria-label={t.customerPortal.userDetails.fields.company}
                  label={t.customerPortal.userDetails.fields.company}
                  value={form.company}
                  onChange={() => undefined}
                  options={
                    form.company
                      ? [{ key: form.company, label: form.company }]
                      : []
                  }
                  isDisabled
                  className="bg-neutral/5"
                />
                <Select
                  aria-label={
                    t.customerPortal.userDetails.fields.contactLocation
                  }
                  label={t.customerPortal.userDetails.fields.contactLocation}
                  value={form.contactLocation}
                  onChange={() => undefined}
                  options={
                    form.contactLocation
                      ? [
                          {
                            key: form.contactLocation,
                            label: form.contactLocation,
                          },
                        ]
                      : []
                  }
                  isDisabled
                  className="bg-neutral/5"
                />
              </div>
            </Col>
          </FormSection>
        </SettingsContent>
      </Col>

      <SettingsContent size="medium" className="border-t border-neutral/15">
        <Row xEnd gap2 className="p-5">
          <Button
            variant="outline"
            onPress={handleDiscard}
            isDisabled={isSaving}
          >
            {t.customerPortal.actions.discard}
          </Button>
          <Button
            variant="solid"
            color="primary"
            onPress={handleSave}
            isDisabled={isSaving || !isFormDirty}
          >
            {t.customerPortal.actions.save}
          </Button>
        </Row>
      </SettingsContent>
    </Col>
  );
};
